import { ValueEditorSelect, ValueEditorSelectProps } from '@procureai/query-builder-ui-mui';
import { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { I18nNamespace } from '~/common/types';
import { isNumber } from '~/util/number';
import { isString } from '~/util/string';

type Methods = {
  simplifyValue: (value: any | any[] | null) => string | string[] | null;
  getOptionSelected: (option: any, value: any) => boolean;
};

const methods: [Methods, Methods] = [
  // single
  {
    simplifyValue: (value) => (value ? value.key : value),
    getOptionSelected: (option, value) => option.key === value,
  },
  // multiple
  {
    simplifyValue: (value: any[]) => {
      return value
        ? value.map((item) => {
            return typeof item === 'object' ? item.key : item;
          })
        : value;
    },
    getOptionSelected: (option, value) => {
      return option.key === value;
    },
  },
];

export interface ValueEditorSelectAutocompleteProps extends Omit<ValueEditorSelectProps, 'options'> {
  initialI18nPrefix: string;
  initialOptions: (string | number)[];
  ns?: I18nNamespace;
}

export const ValueEditorSelectAutocomplete = ({
  initialI18nPrefix,
  initialOptions,
  ns,
  value,
  onChange,
  ...otherProps
}: ValueEditorSelectAutocompleteProps) => {
  const { t } = useTranslation(ns, { keyPrefix: initialI18nPrefix });

  const options = useMemo(() => {
    return initialOptions
      .map((key) => ({
        key,
        value: t(`${key}`),
      }))
      .sort((a, b) => -b.value.localeCompare(a.value));

    // left out initialOptions and initialI18nPrefix on purpose as changing them should not trigger update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  const { simplifyValue, getOptionSelected } = methods[Number(!!otherProps.multiple)];

  const handleChange = useCallback(
    (value: any) => {
      onChange(simplifyValue(value));
    },
    [onChange, simplifyValue],
  );

  return (
    <ValueEditorSelect
      {...otherProps}
      value={value}
      getOptionLabel={(option) => {
        if (isString(option) || isNumber(option)) {
          return t(`${option}`);
        }
        return option?.value || '';
      }}
      getOptionSelected={getOptionSelected}
      options={options}
      onChange={handleChange as any}
    />
  );
};
